import type { ArgoCDImageUpdateProperties } from "@octopusdeploy/legacy-action-properties";
import { GetNamedPackageReferences, ActionExecutionLocation } from "@octopusdeploy/octopus-server-client";
import type { PropsWithChildren } from "react";
import * as React from "react";
import { useFeedsFromContext, useRefreshFeedsFromContext } from "~/areas/projects/components/Process/Contexts/ProcessFeedsContextProvider";
import { TargetRoles } from "~/areas/projects/components/Process/types";
import { buildRelatedTriggerDependencies } from "~/areas/projects/components/Triggers/ProcessCallouts/triggerPackageRelations";
import type { ActionSummaryProps } from "~/components/Actions/actionSummaryProps";
import type { ActionWithFeeds } from "~/components/Actions/commonActionHelpers";
import { ContainerImageReferenceList } from "~/components/Actions/packageReferences";
import pluginRegistry from "~/components/Actions/pluginRegistry";
import type { ActionEditProps } from "~/components/Actions/pluginRegistry";
import type { ScriptPackageProperties } from "~/components/Actions/script/ScriptPackageReferenceDialog";
import { BaseComponent } from "~/components/BaseComponent/BaseComponent";
import ExternalLink from "~/components/Navigation/ExternalLink/index";
import ExpandableFormSection from "~/components/form/Sections/ExpandableFormSection";
import FormSection from "~/components/form/Sections/FormSection";
import ExpanderSectionHeading from "~/components/form/Sections/FormSectionHeading";
import Summary from "~/components/form/Sections/Summary";
import { VariableLookupText } from "~/components/form/VariableLookupText";
import { Note } from "~/components/form/index";
class ArgoCDImageUpdateActionSummary extends BaseComponent<ActionSummaryProps, never> {
    constructor(props: ActionSummaryProps) {
        super(props);
    }
    render() {
        return <div>Update ArgoCD applications with new container image versions.</div>;
    }
    static displayName = "ArgoCDImageUpdateActionSummary";
}
type ArgoCDImageUpdateActionProps = ActionEditProps<ArgoCDImageUpdateProperties, ScriptPackageProperties>;
type ArgoCDImageUpdateActionInternalProps = ArgoCDImageUpdateActionProps & ActionWithFeeds;
export class ArgoCDImageUpdateActionEdit extends BaseComponent<ArgoCDImageUpdateActionInternalProps, {}> {
    constructor(props: ArgoCDImageUpdateActionInternalProps) {
        super(props);
    }
    packageReferenceSummary = () => {
        const namedPackageReferences = GetNamedPackageReferences(this.props.packages);
        if (namedPackageReferences.length === 0) {
            return Summary.placeholder("No additional packages referenced");
        }
        return Summary.summary(`${namedPackageReferences.length} package referencess`);
    };
    render() {
        const packageReferences = GetNamedPackageReferences(this.props.packages);
        return (<div>
                <ExpanderSectionHeading title="Input" description="When this step runs, Octopus will update the specified image tag in your configuration files."/>
                <ExpandableFormSection title={"Referenced Container Images"} isExpandedByDefault={this.props.expandedByDefault} errorKey="Octopus.Action.Script.Packages" summary={this.packageReferenceSummary()} help={"Add the container image(s) that Octopus will update during deployment."}>
                    <Note>
                        Learn more about <ExternalLink href="ScriptStepPackageReferences">package/container references</ExternalLink>.
                    </Note>
                    <ContainerImageReferenceList packageReferences={packageReferences} relatedTriggersDependencies={buildRelatedTriggerDependencies(this.props.inputDependencies)} {...this.props}/>
                </ExpandableFormSection>
                <ExpanderSectionHeading title="Output" description="A new version of your configuration files with the updated image tag will be commited to Git, which will trigger a sync from Argo CD."/>
                <FormSection title={"Argo CD Source Repository"} help={"Octopus will get the source repository details from your connected Argo CD instances."}/>
                <ExpandableFormSection errorKey="Octopus.Action.ArgoCD.CommitMessage" title="Commit Message" summary={!!this.props.properties["Octopus.Action.ArgoCD.CommitMessage"] ? Summary.summary("Custom message provided") : Summary.summary("Default message used")} help={"Provide a message for the commit to Git."} isExpandedByDefault={this.props.expandedByDefault}>
                    <VariableLookupText key="Octopus.Action.UpdateImageVersion.Destination.CommitMessage" localNames={this.props.localNames} value={this.props.properties["Octopus.Action.ArgoCD.CommitMessage"] ?? ""} onChange={(filePaths) => this.props.setProperties({ ["Octopus.Action.ArgoCD.CommitMessage"]: filePaths })} label={"Commit Message"} placeholder={"Octopus Deployment\nRelease #{Octopus.Release.Number} for #{Octopus.Environment.Name}"} error={this.props.getFieldError("Octopus.Action.UpdateImageVersion.Destination.CommitMessage")} multiline={true}/>
                </ExpandableFormSection>
            </div>);
    }
    static displayName = "ArgoCDImageUpdateActionEdit";
}
export function ArgoCDImageUpdateAction(props: PropsWithChildren<ArgoCDImageUpdateActionProps>) {
    const feeds = useFeedsFromContext();
    const refreshFeeds = useRefreshFeedsFromContext();
    return <ArgoCDImageUpdateActionEdit {...props} feeds={feeds} refreshFeeds={refreshFeeds}/>;
}
pluginRegistry.registerAction({
    executionLocation: ActionExecutionLocation.AlwaysOnServer,
    canRunOnWorker: false,
    canRunInContainer: false,
    actionType: "Octopus.ArgoCD.ImageUpdate",
    summary: (properties, targetRolesAsCSV) => <ArgoCDImageUpdateActionSummary properties={properties} targetRolesAsCSV={targetRolesAsCSV}/>,
    editSections: {
        //top: (props: ActionEditProps<UpdateImageVersionProperties>) => ,
        default: (props: ArgoCDImageUpdateActionProps) => <ArgoCDImageUpdateAction {...props}/>,
    },
    canHaveChildren: (step) => false,
    canBeChild: true,
    targetRoleOption: (action) => TargetRoles.None,
    hasPackages: (action) => true,
    docsLink: "updateArgoCDAppImages",
});
